// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for CO-5 text-only CLD variable labels. In a causal-loop
// diagram the variable *is* the text -- there's no stock rectangle or aux
// circle, just the name centered on the node with links docking at the edges
// of its (invisible) bounding box (see the 260803 loop-rendering reference).
//
// This module is the pure, canvas-free part: turn a variable's display name
// into wrapped lines and estimate the box those lines occupy, so CLDVariable
// can center the text and size an invisible hit/port box without measuring the
// DOM. Kept separate from the React component so it unit-tests directly.

/** Default max characters per wrapped line. Tuned so multi-word names like
 * "Potential Customers" break to two short lines rather than one wide one. */
export const DEFAULT_MAX_CHARS = 16;

/** Approximate advance width of one character at the CLD label font size.
 * Inter/Arial 13px averages ~6.7px; we round up so the estimated box never
 * clips the glyphs (an under-estimate would dock links inside a word). */
export const DEFAULT_CHAR_WIDTH = 7;
/** Line-box height at the CLD label font size (13px text, ~16px leading). */
export const DEFAULT_LINE_HEIGHT = 16;
/** Horizontal padding from the longest line to the box edge (per side). */
export const DEFAULT_PAD_X = 8;
/** Vertical padding from the text block to the box edge (per side). */
export const DEFAULT_PAD_Y = 5;

export interface LabelBox {
  readonly width: number;
  readonly height: number;
}

export interface LabelBoxOptions {
  readonly charWidth?: number;
  readonly lineHeight?: number;
  readonly padX?: number;
  readonly padY?: number;
}

/**
 * Wrap a display name into lines no longer than `maxChars` characters, greedy
 * word-by-word. Existing newlines in the name are honored as hard breaks
 * (displayName already turns literal `\n` into newlines); each resulting
 * segment is then word-wrapped independently. A single word longer than
 * `maxChars` is left intact on its own line rather than hard-split mid-word --
 * variable names read better unbroken, and the invisible box grows to fit.
 */
export function wrapLabel(text: string, maxChars: number = DEFAULT_MAX_CHARS): string[] {
  const out: string[] = [];
  for (const segment of text.split('\n')) {
    const words = segment.split(/\s+/).filter((w) => w.length > 0);
    if (words.length === 0) {
      continue;
    }
    let cur = '';
    for (const w of words) {
      if (cur === '') {
        cur = w;
      } else if ((cur + ' ' + w).length <= maxChars) {
        cur += ' ' + w;
      } else {
        out.push(cur);
        cur = w;
      }
    }
    out.push(cur);
  }
  // An all-whitespace / empty name still needs one (empty) line so the caller
  // renders a hit box rather than nothing.
  return out.length > 0 ? out : [''];
}

/**
 * Estimate the bounding box for a block of wrapped lines centered on a node.
 * Width tracks the longest line; height tracks the line count. Both include
 * padding so links dock just outside the glyphs, not touching them.
 */
export function cldLabelBox(lines: readonly string[], options: LabelBoxOptions = {}): LabelBox {
  const charWidth = options.charWidth ?? DEFAULT_CHAR_WIDTH;
  const lineHeight = options.lineHeight ?? DEFAULT_LINE_HEIGHT;
  const padX = options.padX ?? DEFAULT_PAD_X;
  const padY = options.padY ?? DEFAULT_PAD_Y;
  const maxLen = lines.reduce((m, l) => (l.length > m ? l.length : m), 0);
  return {
    width: maxLen * charWidth + 2 * padX,
    height: Math.max(1, lines.length) * lineHeight + 2 * padY,
  };
}
